import clsx from "clsx";
import { User, Route, X } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import type { WatchlistScope } from "../../watchlist/types";
import type { FilterOption } from "../FilterMultiSelect";

type ScopeChipsProps = {
  scope: WatchlistScope;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
  onChange: (scope: WatchlistScope) => void;
  className?: string;
};

/**
 * What a chip carries, which is also how it is tinted: people and processes are
 * told apart without reading the labels, and `meta` is for the parts of a scope
 * that are not a name — a window, or a "everyone" standing in for a list.
 */
export type ChipTone = "person" | "process" | "meta";

const TONE_CLASS: Record<ChipTone, string> = {
  person: "bg-muted-surface text-text",
  process: "bg-info/10 text-info",
  meta: "bg-muted-surface text-text-mid",
};

/**
 * One piece of scope, as a chip. Long names truncate rather than fill the row —
 * the full one stays in the tooltip.
 */
export const ScopeChip = ({
  tone,
  icon,
  label,
  onRemove,
}: {
  tone: ChipTone;
  icon: React.ReactNode;
  label: string;
  /** Omitted where the chip is only being read, which is most places. */
  onRemove?: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <span
      title={label}
      className={clsx(
        "flex max-w-[12rem] items-center gap-1 rounded-lg px-2 py-0.5 text-xs",
        TONE_CLASS[tone],
      )}
    >
      <span className={clsx("shrink-0", tone !== "process" && "text-text-dim")}>{icon}</span>
      <span className="min-w-0 truncate">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          title={t("watchlist.remove_from_scope")}
          className={clsx(
            "shrink-0 transition",
            tone === "process" ? "opacity-70 hover:opacity-100" : "text-text-dim hover:text-text",
          )}
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
};

/**
 * Whether a scope names every person on offer, which reads as "All people" rather than as a
 * list of everyone. False while the options are still loading, when nobody is on offer.
 */
export const coversEveryone = (scope: WatchlistScope, userOptions: FilterOption[]): boolean =>
  userOptions.length > 0 && userOptions.every(option => scope.userIds.includes(option.id));

/**
 * The names in a scope, each removable: what the prompt will be answered
 * against, beside the box it is written in. Only names — the window and the
 * cadence are pills of their own here, because they are always set to
 * something and so have nothing to add and remove.
 */
const ScopeChips = ({
  scope,
  userOptions,
  processOptions,
  onChange,
  className,
}: ScopeChipsProps) => {
  const { t } = useTranslation();
  // Everyone stands in for the list, as on the trigger, and like the last person it is not
  // removable.
  const allPeople = coversEveryone(scope, userOptions);
  const people = allPeople ? [] : userOptions.filter(option => scope.userIds.includes(option.id));
  // An empty selection means every process, which the trigger already says: only
  // a genuine subset is worth spelling out as chips.
  const processes =
    scope.processSlugs.length < processOptions.length
      ? processOptions.filter(option => scope.processSlugs.includes(option.id))
      : [];

  const chips = [
    ...(allPeople
      ? [
          {
            key: "people-all",
            tone: "meta" as const,
            icon: <User size={12} />,
            label: t("watchlist.all_people"),
          },
        ]
      : []),
    ...people.map(person => ({
      key: `person-${person.id}`,
      tone: "person" as const,
      icon: <User size={12} />,
      label: person.label,
      // The last person is not removable: an empty scope reads nothing.
      onRemove:
        scope.userIds.length > 1
          ? () => onChange({ ...scope, userIds: scope.userIds.filter(id => id !== person.id) })
          : undefined,
    })),
    ...processes.map(process => ({
      key: `process-${process.id}`,
      tone: "process" as const,
      icon: <Route size={12} />,
      label: process.label,
      onRemove: () =>
        onChange({
          ...scope,
          processSlugs: scope.processSlugs.filter(slug => slug !== process.id),
          allProcesses: false,
        }),
    })),
  ];

  // Nothing to show is nothing to lay out: the caller's spacing would otherwise
  // open a gap above a row that is not there.
  if (chips.length === 0) return null;

  return (
    <div className={clsx("flex flex-wrap items-start gap-1.5", className)}>
      {chips.map(({ key, ...chip }) => (
        <ScopeChip key={key} {...chip} />
      ))}
    </div>
  );
};

export default ScopeChips;
